'use client';

import { useState, useCallback, useRef, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API_BASE = process.env.NEXT_PUBLIC_PHARMA_API_URL || '';

interface ChartConfig {
  type: 'kpi' | 'bar' | 'line' | 'pie';
  x_col: string;
  y_cols: string[];
  time_cols: string[];
}

interface QueryResult {
  sql: string;
  columns: string[];
  data: Record<string, unknown>[];
  chart_config: ChartConfig;
  row_count: number;
  elapsed_ms: number;
  cached: boolean;
  _forecastCount?: number;
  _forecastKPIs?: { label: string; value: number; col: string }[];
}

const QUICK_QUERIES = [
  {
    id: 'realtime',
    category: 'Real-Time Operations',
    queries: [
      { label: "Today's KPIs", q: "What is today's total revenue, profit, and units sold?" },
      { label: "Today by Country", q: "Show today's revenue by country" },
      { label: "Top Products Today", q: "What are today's top products by revenue?" },
    ],
  },
  {
    id: 'inventory',
    category: 'Inventory',
    queries: [
      { label: "Top 5 Highest Stock", q: "Top 5 products with highest stocks in inventory" },
      { label: "Top 5 Lowest Stock",  q: "Top 5 products with lowest stocks in inventory" },
    ],
  },
  {
    id: 'products',
    category: 'Product Analytics',
    queries: [
      { label: "By Therapeutic Area", q: "Revenue by therapeutic area" },
      { label: "Dosage Form Margins", q: "Profit margin by dosage form" },
      { label: "Top 5 Products", q: "Top 5 products by revenue" },
    ],
  },
  {
    id: 'customers',
    category: 'Customer Intelligence',
    queries: [
      { label: "By Customer Type", q: "Revenue by customer type" },
      { label: "Top Customers", q: "Top 10 customers by revenue this year" },
    ],
  },
  {
    id: 'trends',
    category: 'Financial Trends',
    queries: [
      { label: "Monthly Revenue", q: "Monthly revenue trend" },
      { label: "Quarterly Profit", q: "Profit trend by quarter" },
      { label: "Year by Region", q: "This year's revenue by region" },
    ],
  },
];

// ── Prediction / forecasting helpers ─────────────────────────────

const PREDICT_KW = ['predict', 'forecast', 'tomorrow', 'next week', 'next month', 'next quarter', 'future', 'projection'];

function isPrediction(q: string) {
  const l = q.toLowerCase();
  return PREDICT_KW.some((k) => l.includes(k));
}

// ── Advisory / suggestion detection ──────────────────────────────

const ADVISORY_KW = ['how to', 'how can', 'how do', 'improve', 'better', 'suggest', 'recommend', 'strategy', 'tips', 'ways to', 'advice', 'optimize', 'increase', 'boost', 'what can', 'what should', 'help me', 'should i'];

function isAdvisory(q: string) {
  const l = q.toLowerCase();
  return ADVISORY_KW.some((k) => l.includes(k));
}

function generateSuggestions(_question: string, result: QueryResult): string[] {
  const { data, columns, chart_config } = result;
  if (data.length === 0) return [];
  const { x_col, y_cols } = chart_config;
  const yCol = y_cols[0];
  const xCol = x_col;

  // ── Single-row KPI result (e.g. "how to make germany sales better" → totals) ──
  if (data.length === 1) {
    const row = data[0];
    const tips: string[] = [];
    const numCols = columns.filter(c => typeof row[c] === 'number');
    if (numCols.length === 0) return [];

    const revenue = columns.find(c => c.includes('revenue'));
    const profit  = columns.find(c => c.includes('profit'));
    const units   = columns.find(c => c.includes('unit') || c.includes('quantity'));

    const rev = revenue ? row[revenue] as number : null;
    const pft = profit  ? row[profit]  as number : null;
    const qty = units   ? row[units]   as number : null;

    if (rev !== null) {
      tips.push(`Current revenue is ${formatValue(rev)}. To grow it, focus on expanding the highest-margin product lines, running targeted promotions on top-selling SKUs, and entering adjacent customer segments or geographies.`);
    }
    if (rev !== null && pft !== null) {
      const margin = Math.round((pft / rev) * 100);
      if (margin < 20) {
        tips.push(`Profit margin is ${margin}% — below the 20% pharma benchmark. Review product mix, renegotiate supplier costs, or adjust pricing on low-margin SKUs to improve bottom-line performance.`);
      } else {
        tips.push(`Profit margin is ${margin}% — healthy for the sector. Protect pricing power on premium products and control cost growth as you scale volume to sustain this level.`);
      }
    }
    if (qty !== null && rev !== null && qty > 0) {
      const perUnit = Math.round(rev / qty);
      tips.push(`Average revenue per unit is ${formatValue(perUnit)}. Shifting product mix toward higher-value formats (e.g. branded over generic, specialist over OTC) can grow revenue without needing volume increases.`);
    }
    if (tips.length === 0) {
      tips.push(`To improve performance in this segment, identify which products or regions are underperforming, review pricing strategy, and strengthen distribution in areas with low coverage.`);
    }
    return tips;
  }

  // ── Multi-row result (ranked comparison) ────────────────────────
  if (!yCol || !xCol) return [];
  const numericRows = data.filter(r => typeof r[yCol] === 'number');
  if (numericRows.length < 2) return [];

  const sorted = [...numericRows].sort((a, b) => (b[yCol] as number) - (a[yCol] as number));
  const total = numericRows.reduce((s, r) => s + (r[yCol] as number), 0);
  const avg = total / numericRows.length;
  const topN = Math.min(3, sorted.length);
  const top = sorted.slice(0, topN);
  const bottom = sorted.slice(-Math.min(3, Math.max(0, sorted.length - topN)));
  const metric = formatLabel(yCol);
  const dim = formatLabel(xCol);
  const tips: string[] = [];

  const topNames = top.map(r => String(r[xCol])).join(', ');
  tips.push(`Double down on top performers — ${topNames} lead ${metric} (best: ${formatValue(top[0][yCol])}). Prioritise these in marketing spend, supply allocation, and sales incentives.`);

  if (sorted.length > 3) {
    const botNames = bottom.map(r => String(r[xCol])).join(', ');
    tips.push(`Investigate underperformers — ${botNames} sit at the bottom (lowest: ${formatValue(bottom[bottom.length - 1][yCol])}). Determine whether the cause is pricing, distribution, awareness, or product fit before cutting investment.`);
  }

  if (total > 0) {
    const topShare = Math.round(((top[0][yCol] as number) / total) * 100);
    if (topShare > 35) {
      tips.push(`Reduce concentration risk — ${String(top[0][xCol])} drives ${topShare}% of total ${metric}. Diversifying growth reduces exposure if this single driver softens.`);
    } else {
      tips.push(`Your ${metric} is healthy across ${dim}s (average: ${formatValue(avg)}). Sustain this balance while gradually lifting the middle tier closer to the top performers.`);
    }
  }

  const aboveAvg = sorted.filter(r => (r[yCol] as number) > avg * 1.2).length;
  const belowAvg = sorted.filter(r => (r[yCol] as number) < avg * 0.8).length;
  if (aboveAvg > 0 && belowAvg > 0) {
    tips.push(`${aboveAvg} ${dim}${aboveAvg > 1 ? 's' : ''} outperform the average by 20%+ while ${belowAvg} underperform by 20%+. Rebalancing budget toward high-performers may yield faster ${metric} growth.`);
  }

  return tips;
}

function deriveHistoricalQuestion(q: string): { question: string; granularity: 'daily' | 'monthly' | 'quarterly' } {
  const l = q.toLowerCase();
  let granularity: 'daily' | 'monthly' | 'quarterly' = 'monthly';
  if (l.includes('day') || l.includes('daily') || l.includes('tomorrow') || l.includes('yesterday')) granularity = 'daily';
  else if (l.includes('quarter')) granularity = 'quarterly';
  let metric = 'revenue';
  if (l.includes('profit')) metric = 'profit';
  else if (l.includes('unit') || l.includes('quantity') || l.includes('sold') || l.includes('sale')) metric = 'sales';
  const map = {
    daily: `Show total ${metric} by date`,
    monthly: `Monthly ${metric} trend`,
    quarterly: `${metric} trend by quarter`,
  };
  return { question: map[granularity], granularity };
}

function buildForecast(result: QueryResult, periodsAhead: number): QueryResult {
  const { x_col, y_cols, time_cols } = result.chart_config;
  const yCol = y_cols[0];
  const xCol = x_col || (time_cols[0] ?? '');
  if (!xCol || !yCol || result.data.length < 3) return result;

  // Regression over ALL historical data for maximum accuracy
  const allPts = result.data.map((row, i) => ({
    x: i,
    y: typeof row[yCol] === 'number' ? (row[yCol] as number) : 0,
  }));
  const n = allPts.length;
  const sx = allPts.reduce((a, p) => a + p.x, 0);
  const sy = allPts.reduce((a, p) => a + p.y, 0);
  const sxy = allPts.reduce((a, p) => a + p.x * p.y, 0);
  const sx2 = allPts.reduce((a, p) => a + p.x * p.x, 0);
  const den = n * sx2 - sx * sx;
  const slope = den !== 0 ? (n * sxy - sx * sy) / den : 0;
  const intercept = (sy - slope * sx) / n;

  // Limit chart display to last 60 data points to avoid cluttered line charts (e.g. daily data)
  const DISPLAY_LIMIT = 60;
  const displayData = result.data.length > DISPLAY_LIMIT ? result.data.slice(-DISPLAY_LIMIT) : result.data;

  const last = result.data[n - 1];
  const forecastRows: Record<string, unknown>[] = [];
  const _forecastKPIs: { label: string; value: number; col: string }[] = [];
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  for (let i = 1; i <= periodsAhead; i++) {
    const predicted = Math.round(Math.max(0, slope * (n - 1 + i) + intercept) * 100) / 100;
    const row: Record<string, unknown> = { ...last, [yCol]: predicted };
    let label = `Forecast +${i}`;
    if (xCol === 'month') {
      const lm = typeof last['month'] === 'number' ? (last['month'] as number) : 12;
      const ly = typeof last['year'] === 'number' ? (last['year'] as number) : 2024;
      const nm = ((lm - 1 + i) % 12) + 1;
      const ny = ly + Math.floor((lm - 1 + i) / 12);
      row['month'] = nm; row['year'] = ny;
      label = `${MONTHS[nm - 1]} ${ny}`;
    } else if (xCol === 'quarter') {
      const lq = typeof last['quarter'] === 'number' ? (last['quarter'] as number) : 4;
      const ly = typeof last['year'] === 'number' ? (last['year'] as number) : 2024;
      const nq = ((lq - 1 + i) % 4) + 1;
      const ny = ly + Math.floor((lq - 1 + i) / 4);
      row['quarter'] = nq; row['year'] = ny;
      label = `Q${nq} ${ny}`;
    } else if (xCol === 'date') {
      row[xCol] = `Forecast +${i}d`;
      label = `+${i} day${i > 1 ? 's' : ''}`;
    } else {
      row[xCol] = label;
    }
    forecastRows.push(row);
    _forecastKPIs.push({ label, value: predicted, col: yCol });
  }

  const combined = [...displayData, ...forecastRows];
  return {
    ...result,
    data: combined,
    row_count: combined.length,
    chart_config: { ...result.chart_config, type: 'line' as const },
    _forecastCount: periodsAhead,
    _forecastKPIs,
  };
}

// ─────────────────────────────────────────────────────────────────

const DATA_COLORS = ['#C5A572', '#2E5A8F', '#7C9BC7', '#A18A5E', '#4B7EBD', '#D4BC96'];

function formatValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    if (Math.abs(val) >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
    if (Math.abs(val) >= 1_000) return `${(val / 1_000).toFixed(1)}K`;
    if (!Number.isInteger(val)) return val.toFixed(2);
    return String(val);
  }
  return String(val);
}

function formatLabel(col: string): string {
  return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function prepareChartData(
  data: Record<string, unknown>[],
  xCol: string,
  timeCols: string[]
): Record<string, unknown>[] {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return data.map((row) => {
    const entry = { ...row };
    if (xCol === 'month' && timeCols.includes('year')) {
      const m = Number(row['month']);
      entry['_label'] = `${MONTHS[m - 1] ?? m} ${row['year']}`;
    } else if (xCol === 'quarter' && timeCols.includes('year')) {
      entry['_label'] = `Q${row['quarter']} ${row['year']}`;
    } else {
      entry['_label'] = row[xCol];
    }
    return entry;
  });
}

function CustomTooltip({ active, payload, label }: { active?: boolean; payload?: { color: string; dataKey: string; value: unknown }[]; label?: string }) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-lg px-4 py-3 shadow-2xl text-sm"
      style={{ background: '#0D1B2E', border: '1px solid rgba(197,165,114,0.3)' }}
    >
      {label && (
        <div className="mb-2 pb-2" style={{ borderBottom: '1px solid rgba(197,165,114,0.15)', color: 'rgba(248,246,243,0.5)', fontSize: 11 }}>
          {label}
        </div>
      )}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: p.color }} />
          <span style={{ color: 'rgba(248,246,243,0.7)' }}>{formatLabel(p.dataKey)}:</span>
          <span className="font-semibold ml-auto pl-3" style={{ color: '#F8F6F3' }}>
            {formatValue(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

const AXIS_TICK_STYLE = { fill: 'rgba(248,246,243,0.4)', fontSize: 11, fontFamily: 'Inter, sans-serif' };

function KPICards({ data, columns }: { data: Record<string, unknown>[]; columns: string[] }) {
  const row = data[0] ?? {};
  const metricCols = columns.filter((c) => {
    const v = row[c];
    return typeof v === 'number';
  });
  const displayCols = metricCols.length > 0 ? metricCols : columns;

  return (
    <div className="px-6 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {displayCols.map((col) => (
        <div
          key={col}
          className="rounded-xl p-5"
          style={{ background: 'rgba(197,165,114,0.06)', border: '1px solid rgba(197,165,114,0.18)' }}
        >
          <p
            className="text-xs font-semibold uppercase tracking-widest mb-3"
            style={{ color: 'rgba(248,246,243,0.4)' }}
          >
            {formatLabel(col)}
          </p>
          <p className="font-serif text-3xl font-bold" style={{ color: '#C5A572' }}>
            {formatValue(row[col])}
          </p>
        </div>
      ))}
    </div>
  );
}

function DataChart({ result }: { result: QueryResult }) {
  const { chart_config, data, columns } = result;
  const { type, x_col, y_cols, time_cols } = chart_config;

  if (!data.length || type === 'kpi') {
    return type === 'kpi' ? <KPICards data={data} columns={columns} /> : null;
  }

  const chartData = prepareChartData(data, x_col, time_cols);
  const xKey = x_col === 'month' || x_col === 'quarter' ? '_label' : x_col;
  const hasMany = chartData.length > 6;
  const isSingle = y_cols.length === 1;

  if (type === 'line') {
    return (
      <div className="px-5 pt-2 pb-3" style={{ height: 300 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(197,165,114,0.08)" vertical={false} />
            <XAxis dataKey={xKey} tick={AXIS_TICK_STYLE} tickLine={false}
              axisLine={{ stroke: 'rgba(197,165,114,0.15)' }}
              angle={hasMany ? -30 : 0} textAnchor={hasMany ? 'end' : 'middle'} height={hasMany ? 50 : 30} />
            <YAxis tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} tickFormatter={(v) => formatValue(v)} width={60} />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(248,246,243,0.5)', paddingTop: 8 }} />
            {y_cols.map((col, i) => (
              <Line key={col} type="monotone" dataKey={col} stroke={DATA_COLORS[i]} strokeWidth={2}
                dot={{ fill: DATA_COLORS[i], r: 3, strokeWidth: 0 }} activeDot={{ r: 5, strokeWidth: 0 }} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'pie') {
    return (
      <div className="px-5 pt-2 pb-3" style={{ height: 310 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={chartData} dataKey={y_cols[0]} nameKey={xKey}
              cx="50%" cy="50%" outerRadius={110} innerRadius={58} paddingAngle={2}>
              {chartData.map((_, i) => <Cell key={i} fill={DATA_COLORS[i % DATA_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(248,246,243,0.5)', paddingTop: 8 }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Bar chart — proportional bar width + per-bar palette for single-series
  const barSize = Math.max(28, Math.min(68, Math.floor(520 / chartData.length)));
  return (
    <div className="px-5 pt-2 pb-3" style={{ height: 300 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: hasMany ? 40 : 12, left: 0 }}
          barSize={barSize} barCategoryGap="25%">
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(197,165,114,0.08)" vertical={false} />
          <XAxis dataKey={xKey} tick={AXIS_TICK_STYLE} tickLine={false}
            axisLine={{ stroke: 'rgba(197,165,114,0.15)' }}
            angle={hasMany ? -35 : 0} textAnchor={hasMany ? 'end' : 'middle'}
            height={hasMany ? 55 : 30} interval={0} />
          <YAxis tick={AXIS_TICK_STYLE} tickLine={false} axisLine={false} tickFormatter={(v) => formatValue(v)} width={60} />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(197,165,114,0.04)' }} />
          {!isSingle && <Legend wrapperStyle={{ fontSize: 12, color: 'rgba(248,246,243,0.5)', paddingTop: 8 }} />}
          {y_cols.map((col, seriesIdx) => (
            <Bar key={col} dataKey={col} radius={[3, 3, 0, 0]} fill={DATA_COLORS[seriesIdx]}>
              {isSingle && chartData.map((_, j) => (
                <Cell key={j} fill={DATA_COLORS[j % DATA_COLORS.length]} />
              ))}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DataTable({ result }: { result: QueryResult }) {
  const { columns, data, row_count, elapsed_ms, cached } = result;
  const visible = data.slice(0, 50);

  return (
    <div className="mx-5 mb-5 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(197,165,114,0.12)' }}>
      <div
        className="flex items-center justify-between px-4 py-2.5"
        style={{ background: 'rgba(197,165,114,0.07)', borderBottom: '1px solid rgba(197,165,114,0.1)' }}
      >
        <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#C5A572' }}>
          Results
        </span>
        <span className="text-xs" style={{ color: 'rgba(248,246,243,0.35)' }}>
          {row_count} row{row_count !== 1 ? 's' : ''} · {elapsed_ms}ms
          {cached ? ' · cached' : ''}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: 'rgba(197,165,114,0.04)' }}>
              {columns.map((col) => (
                <th
                  key={col}
                  className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider whitespace-nowrap"
                  style={{ color: 'rgba(197,165,114,0.7)', borderBottom: '1px solid rgba(197,165,114,0.1)' }}
                >
                  {formatLabel(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, i) => (
              <tr
                key={i}
                style={{
                  borderBottom: '1px solid rgba(197,165,114,0.06)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(197,165,114,0.02)',
                }}
              >
                {columns.map((col) => (
                  <td
                    key={col}
                    className="px-4 py-3 whitespace-nowrap tabular-nums"
                    style={{ color: 'rgba(248,246,243,0.75)' }}
                  >
                    {formatValue(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {data.length > 50 && (
          <p className="text-center py-3 text-xs" style={{ color: 'rgba(248,246,243,0.3)', borderTop: '1px solid rgba(197,165,114,0.06)' }}>
            Showing 50 of {row_count} rows
          </p>
        )}
      </div>
    </div>
  );
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export default function ExplorePharmaModal({ isOpen, onClose }: Props) {
  const [question, setQuestion] = useState('');
  const [result, setResult] = useState<QueryResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeQuery, setActiveQuery] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set(['realtime']));
  const [showSQL, setShowSQL] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [chartTypeOverride, setChartTypeOverride] = useState<'bar' | 'pie' | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const runQuery = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setActiveQuery(trimmed);
    setShowSQL(false);
    setSidebarOpen(false);
    setChartTypeOverride(null);
    try {
      const isPred = isPrediction(trimmed);
      let question = trimmed;
      let granularity: 'daily' | 'monthly' | 'quarterly' = 'monthly';
      if (isPred) {
        const derived = deriveHistoricalQuestion(trimmed);
        question = derived.question;
        granularity = derived.granularity;
      }
      const res = await fetch(`${API_BASE}/api/query`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { detail?: string };
        throw new Error(body.detail ?? `Request failed (${res.status})`);
      }
      const raw = await res.json() as QueryResult;
      const periodsAhead = granularity === 'daily' ? 1 : granularity === 'quarterly' ? 2 : 3;
      let finalResult = isPred ? buildForecast(raw, periodsAhead) : raw;
      // Override pie→bar by default; respect explicit "donut/pie" request
      const l = trimmed.toLowerCase();
      const wantsDonut = !isPred && /\b(donut|pie|circle|ring)\b/.test(l);
      if (!isPred && finalResult.chart_config.type === 'pie' && !wantsDonut) {
        finalResult = { ...finalResult, chart_config: { ...finalResult.chart_config, type: 'bar' as const } };
      }
      setResult(finalResult);
      // Set initial chart override based on explicit keyword
      if (!isPred) {
        if (wantsDonut) setChartTypeOverride('pie');
        else if (/\bbar\b/.test(l)) setChartTypeOverride('bar');
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to fetch results');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleQuick = useCallback(
    (q: string) => {
      setQuestion(q);
      runQuery(q);
    },
    [runQuery]
  );

  const toggleCategory = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setTimeout(() => inputRef.current?.focus(), 80);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    if (isOpen) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const showTable = result && !loading && result.data.length > 0 && result.chart_config.type !== 'kpi' && result._forecastCount !== 1;

  return (
    <>
      <style>{`
        @keyframes epf-spin { to { transform: rotate(360deg); } }
        @keyframes epf-fadein { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
        .epf-fadein { animation: epf-fadein 0.22s ease both; }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex flex-col"
        style={{ background: '#09172A', fontFamily: 'Inter, sans-serif' }}
      >
        {/* Subtle background texture */}
        <div className="pointer-events-none absolute inset-0 z-0" aria-hidden>
          <div style={{ position: 'absolute', top: '15%', right: '20%', width: 500, height: 500, background: 'radial-gradient(circle, rgba(197,165,114,0.04) 0%, transparent 70%)' }} />
          <div style={{ position: 'absolute', bottom: '10%', left: '10%', width: 400, height: 400, background: 'radial-gradient(circle, rgba(46,90,143,0.06) 0%, transparent 70%)' }} />
        </div>

        {/* ── Header ── */}
        <header
          className="relative z-10 flex items-center justify-between px-5 sm:px-6 py-4 shrink-0"
          style={{ borderBottom: '1px solid rgba(197,165,114,0.13)', background: 'rgba(9,23,42,0.95)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: 'rgba(197,165,114,0.1)', border: '1px solid rgba(197,165,114,0.2)' }}
            >
              <svg className="w-4 h-4" style={{ color: '#C5A572' }} viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 5.5l8.5 3.5v7.2c0 5.2-3.6 9.9-8.5 11.8-4.9-1.9-8.5-6.6-8.5-11.8V9L16 5.5z" />
                <path d="M11.5 15.5l3.2 3.2 6.3-6.3" />
              </svg>
            </div>
            <div>
              <h2 className="font-serif font-bold text-white leading-none" style={{ fontSize: 17 }}>
                Pharma Intelligence Suite
              </h2>
              <p className="mt-0.5" style={{ fontSize: 11, color: 'rgba(197,165,114,0.6)' }}>
                SIMAX EDW · BigQuery
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Mobile sidebar toggle */}
            <button
              className="sm:hidden flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs"
              style={{ color: 'rgba(248,246,243,0.5)', border: '1px solid rgba(197,165,114,0.15)' }}
              onClick={() => setSidebarOpen((v) => !v)}
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              Queries
            </button>

            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs transition-all"
              style={{ color: 'rgba(248,246,243,0.45)', border: '1px solid rgba(197,165,114,0.13)' }}
              onClick={onClose}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = '#F8F6F3';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(197,165,114,0.35)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'rgba(248,246,243,0.45)';
                (e.currentTarget as HTMLElement).style.borderColor = 'rgba(197,165,114,0.13)';
              }}
            >
              Close
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </header>

        {/* ── Body ── */}
        <div className="relative z-10 flex flex-1 min-h-0 overflow-hidden">

          {/* ── Sidebar (desktop always visible, mobile overlay) ── */}
          <nav
            className={`
              shrink-0 overflow-y-auto py-5 px-3
              ${sidebarOpen ? 'flex' : 'hidden'} sm:flex flex-col
              absolute sm:relative inset-0 sm:inset-auto z-20 sm:z-auto
            `}
            style={{
              width: 240,
              borderRight: '1px solid rgba(197,165,114,0.1)',
              background: 'rgba(6,16,30,0.97)',
            }}
          >
            <p
              className="px-3 mb-4 text-xs font-semibold uppercase tracking-widest"
              style={{ color: 'rgba(197,165,114,0.45)', letterSpacing: '0.12em' }}
            >
              Quick Queries
            </p>

            {QUICK_QUERIES.map((group) => {
              const isOpen = expanded.has(group.id);
              return (
                <div key={group.id} className="mb-1">
                  <button
                    onClick={() => toggleCategory(group.id)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ color: isOpen ? 'rgba(248,246,243,0.8)' : 'rgba(248,246,243,0.5)' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = 'rgba(197,165,114,0.05)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <span>{group.category}</span>
                    <svg
                      className="w-3 h-3 transition-transform duration-200"
                      style={{ transform: isOpen ? 'rotate(180deg)' : '', color: 'rgba(197,165,114,0.5)' }}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="mt-0.5 ml-2 space-y-0.5">
                      {group.queries.map((qry) => {
                        const isActive = activeQuery === qry.q;
                        return (
                          <button
                            key={qry.label}
                            onClick={() => handleQuick(qry.q)}
                            className="w-full text-left px-3 py-2 rounded-lg text-sm transition-all"
                            style={{
                              color: isActive ? '#C5A572' : 'rgba(248,246,243,0.5)',
                              background: isActive ? 'rgba(197,165,114,0.09)' : 'transparent',
                              borderLeft: `2px solid ${isActive ? '#C5A572' : 'transparent'}`,
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                (e.currentTarget as HTMLElement).style.color = 'rgba(248,246,243,0.8)';
                                (e.currentTarget as HTMLElement).style.background = 'rgba(197,165,114,0.04)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                (e.currentTarget as HTMLElement).style.color = 'rgba(248,246,243,0.5)';
                                (e.currentTarget as HTMLElement).style.background = 'transparent';
                              }
                            }}
                          >
                            {qry.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* ── Main panel ── */}
          <main className="flex-1 flex flex-col min-w-0 overflow-hidden">

            {/* Search bar */}
            <div
              className="shrink-0 px-5 sm:px-6 py-4"
              style={{ borderBottom: '1px solid rgba(197,165,114,0.09)' }}
            >
              <form
                onSubmit={(e) => { e.preventDefault(); runQuery(question); }}
                className="flex gap-3"
              >
                <div className="flex-1 relative">
                  <svg
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    style={{ color: 'rgba(197,165,114,0.4)' }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    ref={inputRef}
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask anything — e.g. Show today's revenue by region"
                    className="w-full pl-11 pr-4 py-3.5 rounded-xl text-sm outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(197,165,114,0.18)',
                      color: '#F8F6F3',
                    }}
                    onFocus={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(197,165,114,0.45)'; }}
                    onBlur={(e) => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(197,165,114,0.18)'; }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !question.trim()}
                  className="px-5 py-3.5 rounded-xl font-semibold text-sm transition-all shrink-0"
                  style={{
                    background: loading || !question.trim() ? 'rgba(197,165,114,0.25)' : '#C5A572',
                    color: loading || !question.trim() ? 'rgba(27,40,69,0.45)' : '#1B2845',
                    cursor: loading || !question.trim() ? 'not-allowed' : 'pointer',
                  }}
                >
                  {loading ? 'Running…' : 'Run Query'}
                </button>
              </form>
            </div>

            {/* Results area */}
            <div className="flex-1 overflow-y-auto">

              {/* Welcome state */}
              {!result && !loading && !error && (
                <div className="flex flex-col items-center justify-center h-full text-center px-8 py-12">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-5"
                    style={{ background: 'rgba(197,165,114,0.07)', border: '1px solid rgba(197,165,114,0.18)' }}
                  >
                    <svg className="w-6 h-6" style={{ color: '#C5A572' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h3 className="font-serif text-xl font-bold text-white mb-2">
                    Pharmaceutical Data Explorer
                  </h3>
                  <p className="text-sm max-w-sm" style={{ color: 'rgba(248,246,243,0.38)' }}>
                    Query your pharma data warehouse using plain English. Select a query from the sidebar or type above.
                  </p>

                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-lg">
                    {[
                      { label: 'Sales Performance', sub: 'Revenue, profit & units' },
                      { label: 'Inventory Levels', sub: 'Stock & reorder alerts' },
                      { label: 'Financial Trends', sub: 'Monthly & quarterly' },
                    ].map(({ label, sub }) => (
                      <div
                        key={label}
                        className="rounded-xl px-4 py-3.5 text-left"
                        style={{ background: 'rgba(197,165,114,0.04)', border: '1px solid rgba(197,165,114,0.1)' }}
                      >
                        <p className="text-xs font-semibold" style={{ color: 'rgba(197,165,114,0.65)' }}>{label}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'rgba(248,246,243,0.3)' }}>{sub}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Loading state */}
              {loading && (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <div
                    className="w-10 h-10 rounded-full"
                    style={{
                      border: '2px solid rgba(197,165,114,0.12)',
                      borderTopColor: '#C5A572',
                      animation: 'epf-spin 0.75s linear infinite',
                    }}
                  />
                  <p className="text-sm" style={{ color: 'rgba(248,246,243,0.38)' }}>
                    Generating query and fetching results…
                  </p>
                </div>
              )}

              {/* Error state */}
              {error && !loading && (
                <div className="flex flex-col items-center justify-center h-full px-8 text-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-4"
                    style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.18)' }}
                  >
                    <svg className="w-5 h-5" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <p className="font-semibold text-white mb-1 text-sm">Query Failed</p>
                  <p className="text-sm" style={{ color: 'rgba(239,68,68,0.75)' }}>{error}</p>
                  <button
                    className="mt-4 text-xs underline"
                    style={{ color: 'rgba(197,165,114,0.5)' }}
                    onClick={() => { setError(null); setTimeout(() => inputRef.current?.focus(), 50); }}
                  >
                    Try again
                  </button>
                </div>
              )}

              {/* Results */}
              {result && !loading && (
                <div className="epf-fadein">
                  {/* Active question label */}
                  <div className="px-5 sm:px-6 pt-5 pb-2">
                    <p className="text-xs uppercase tracking-widest mb-1" style={{ color: 'rgba(197,165,114,0.45)', letterSpacing: '0.1em' }}>
                      Query Result
                    </p>
                    <p className="text-sm font-medium" style={{ color: 'rgba(248,246,243,0.85)' }}>
                      {activeQuery}
                    </p>
                  </div>

                  {/* No data state */}
                  {result.data.length === 0 && (
                    <div className="px-6 py-10 text-center">
                      <p className="text-sm" style={{ color: 'rgba(248,246,243,0.35)' }}>No data returned for this query.</p>
                    </div>
                  )}

                  {/* Bar / Donut toggle — shown for categorical results only */}
                  {!result._forecastCount && result.chart_config.type !== 'kpi' && result.chart_config.type !== 'line' && result.data.length > 1 && (
                    <div className="flex items-center gap-2 px-5 pb-3">
                      <span className="text-xs" style={{ color: 'rgba(248,246,243,0.28)' }}>View as:</span>
                      {(['bar', 'pie'] as const).map((t) => {
                        const isActive = (chartTypeOverride ?? result.chart_config.type) === t;
                        return (
                          <button key={t} onClick={() => setChartTypeOverride(t)}
                            className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
                            style={{
                              background: isActive ? 'rgba(197,165,114,0.12)' : 'transparent',
                              border: `1px solid ${isActive ? '#C5A572' : 'rgba(197,165,114,0.18)'}`,
                              color: isActive ? '#C5A572' : 'rgba(248,246,243,0.38)',
                            }}>
                            {t === 'bar' ? '▥ Bar' : '◎ Donut'}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Forecast KPI cards */}
                  {result._forecastKPIs && result._forecastKPIs.length > 0 && (
                    <div className="px-5 sm:px-6 pb-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {result._forecastKPIs.map((kpi, i) => (
                        <div key={i} className="rounded-xl p-4" style={{ background: 'rgba(197,165,114,0.06)', border: '1px solid rgba(197,165,114,0.2)' }}>
                          <p className="text-xs uppercase tracking-widest mb-2" style={{ color: 'rgba(248,246,243,0.38)', letterSpacing: '0.1em' }}>
                            {result._forecastCount === 1 ? "Tomorrow's Prediction" : `Forecast — ${kpi.label}`}
                          </p>
                          <p className="font-serif text-2xl font-bold" style={{ color: '#C5A572' }}>
                            {formatValue(kpi.value)}
                          </p>
                          <p className="text-xs mt-1" style={{ color: 'rgba(197,165,114,0.45)' }}>
                            {result._forecastCount === 1
                              ? `Predicted daily ${formatLabel(kpi.col).toLowerCase()} · linear regression`
                              : `${formatLabel(kpi.col)} · linear regression`}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Chart / KPI — skip historical chart for tomorrow-only forecasts */}
                  {result.data.length > 0 && result._forecastCount !== 1 && (
                    <DataChart result={
                      chartTypeOverride && chartTypeOverride !== result.chart_config.type
                        ? { ...result, chart_config: { ...result.chart_config, type: chartTypeOverride } }
                        : result
                    } />
                  )}

                  {/* Forecast attribution note */}
                  {result._forecastCount && (
                    <p className="px-5 sm:px-6 pb-1 text-xs" style={{ color: 'rgba(197,165,114,0.45)' }}>
                      ◆ Last {result._forecastCount} point{result._forecastCount > 1 ? 's' : ''} are linear regression forecast values based on historical trend
                    </p>
                  )}

                  {/* Data table */}
                  {showTable && (
                    <DataTable result={result} />
                  )}

                  {/* Insights & Recommendations — shown for advisory questions using existing data */}
                  {!result._forecastCount && isAdvisory(activeQuery ?? '') && (() => {
                    const tips = generateSuggestions(activeQuery ?? '', result);
                    if (!tips.length) return null;
                    return (
                      <div className="mx-5 mb-4 rounded-xl overflow-hidden" style={{ border: '1px solid rgba(197,165,114,0.18)' }}>
                        <div className="px-4 py-3" style={{ background: 'rgba(197,165,114,0.07)', borderBottom: '1px solid rgba(197,165,114,0.1)' }}>
                          <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#C5A572' }}>
                            Insights &amp; Recommendations
                          </span>
                        </div>
                        <div className="px-4 py-4 space-y-3">
                          {tips.map((tip, i) => (
                            <div key={i} className="flex gap-3">
                              <span
                                className="shrink-0 mt-0.5 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
                                style={{ background: 'rgba(197,165,114,0.12)', color: '#C5A572' }}
                              >
                                {i + 1}
                              </span>
                              <p className="text-sm leading-relaxed" style={{ color: 'rgba(248,246,243,0.7)' }}>
                                {tip}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  <div className="pb-4" />
                </div>
              )}
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
